import './styles/PermissionGeneral.scss';

import { useQuery } from '@apollo/client';
import { Chip, Grid, Icon, Paper, TextField, Autocomplete } from '@mui/material';

import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import ROLES_QUERY from 'gql/queries/PermissionProvider/getRoles.gql';

import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface Role {
  id: number;
  name: string;
  icon: string;
}

export function PermissionGeneral() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rolesQuery = useQuery<any, any>(ROLES_QUERY, {
    variables: { all: true },
    fetchPolicy: 'no-cache'
  });
  const { setFieldValue, setFieldTouched, values, errors, touched }: any = useFormikContext();

  return (
    <GQLGuard
      query={rolesQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {data => (
        <Grid container spacing={2}>
          <Grid item xs={4} className="permission-detail-modal__body__input">
            <Autocomplete
              options={data.roles as Role[]}
              value={values.authorId && data.roles.find(role => role.id === values.authorId)}
              autoHighlight
              fullWidth
              getOptionLabel={option => option.name}
              renderOption={(props, option) => (
                <li {...props}>
                  <span
                    style={{
                      marginRight: '1em',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Icon className={option.icon + ' permission-detail-modal__body__input__icon'} />
                  </span>
                  {option.name}
                </li>
              )}
              onChange={async (event, value, reason) => {
                setFieldValue('authorId', value?.id, true);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={touched.authorId && errors.authorId}
                  helperText={touched.authorId && errors.authorId}
                  fullWidth
                  required
                  onBlur={() => {
                    if (!touched.authorId) {
                      setFieldTouched('authorId', true);
                    }
                  }}
                  label={t(translations.pages.managePermissionsSettings.authorRole())}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} className="permission-detail-modal__body__input">
            <Autocomplete
              multiple
              options={data.roles as Role[]}
              value={
                values.recipientRoles &&
                values.recipientRoles.map(selectedRole =>
                  data.roles.find(role => {
                    return role.id === selectedRole;
                  })
                )
              }
              getOptionLabel={role => role.name}
              onChange={(role, value, reason) => {
                setFieldValue(
                  'recipientRoles',
                  value.map(role => role.id)
                );
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <span
                    style={{
                      marginRight: '1em'
                    }}
                  >
                    <Icon className={option.icon + ' permission-detail-modal__body__input__icon'} />
                  </span>
                  {option.name}
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  error={touched.recipientRoles && errors.recipientRoles}
                  helperText={touched.recipientRoles && errors.recipientRoles}
                  variant="outlined"
                  label={t(translations.pages.managePermissionsSettings.recipients())}
                  margin="normal"
                  fullWidth
                  onBlur={() => {
                    if (!touched.recipientRoles) {
                      setFieldTouched('recipientRoles', true);
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }}
                />
              )}
              renderTags={(value, getPropTags) => {
                return value.map((role, index) => (
                  <Chip
                    component={Paper}
                    elevation={2}
                    {...getPropTags({ index: index })}
                    key={role.id}
                    icon={
                      <Icon className={role.icon + ' permission-detail-modal__body__input__icon'} />
                    }
                    label={role.name}
                  />
                ));
              }}
            />
          </Grid>
        </Grid>
      )}
    </GQLGuard>
  );
}
